import { motion } from 'motion/react'
import {
  Palette,
  MessageSquare,
  Gauge,
  Workflow,
  Wrench,
  TrendingUp,
  Eye,
  Smartphone,
  Image as ImageIcon,
  MousePointerClick,
  ArrowRight,
} from 'lucide-react'
import { useSEO } from '@/hooks/useSEO'
import { useHeroReveal, useStaggeredGrid, useScrollReveal } from '@gjl/motion/css'

const pillars = [
  {
    icon: Palette,
    title: 'Brand & Design',
    line: 'Does the site look like the institution you are — typography, color, imagery, and hierarchy that match the room you perform in.',
  },
  {
    icon: MessageSquare,
    title: 'Message & Persuasion',
    line: 'Is the value clear in five seconds, with a reason to act on every page.',
  },
  {
    icon: Gauge,
    title: 'Technical Foundation',
    line: 'Speed, accessibility, SEO, mobile, and structured data — measured in a real browser, not assumed.',
  },
  {
    icon: Workflow,
    title: 'Integrations & Workflow',
    line: 'Ticketing, donations, applications, and email connected into the site, not bolted on around it.',
  },
  {
    icon: Wrench,
    title: 'Maintainability & Ownership',
    line: 'Can your team update it without a developer, and do you actually own what you paid for.',
  },
  {
    icon: TrendingUp,
    title: 'Conversion & Growth',
    line: 'Does it turn visitors into ticket buyers, applicants, and donors over a full season.',
  },
]

const checks = [
  {
    icon: Eye,
    title: 'Text legibility over photos',
    line: 'We measure the real contrast of every headline sitting on a hero image. Automated checkers skip this, so illegible white-on-photo text passes most audits.',
  },
  {
    icon: Smartphone,
    title: 'Mobile fit at phone width',
    line: 'Rendered at a real 390px phone: no sideways scrolling, tap targets big enough to hit, text large enough to read.',
  },
  {
    icon: Gauge,
    title: 'Real page speed',
    line: 'Largest Contentful Paint, layout shift, and total page weight, measured on a cold load. These are the numbers Google ranks on.',
  },
  {
    icon: ImageIcon,
    title: 'Image weight',
    line: 'Photos sized and compressed for the web, not multi-megabyte originals that crawl on a phone and burn data.',
  },
  {
    icon: MousePointerClick,
    title: 'Buttons that actually work',
    line: 'Every call to action is click-tested, so nothing on the page quietly does nothing.',
  },
]

export default function HowWeMeasurePage() {
  useSEO({
    title: 'How We Measure Quality — The Full Score',
    description:
      'The Full Score is our six-pillar website standard. We measure the things builders fake and templates miss — real page speed, contrast over photos, mobile fit, and image weight — in a real browser, on your site and ours.',
    url: '/how-we-measure',
    canonical: '/how-we-measure',
  })

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const pillarsRef = useStaggeredGrid<HTMLDivElement>('.pillar-card', { stagger: 0.1 })
  const checksRef = useStaggeredGrid<HTMLDivElement>('.check-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-16 px-4 py-12">
      <section ref={heroRef} className="space-y-6 text-center">
        <p className="text-primary text-sm font-semibold tracking-[0.18em] uppercase">The Full Score</p>
        <h1 className="text-fluid-h1">How we measure quality</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
          Most audits grade one thing, like speed or SEO. The Full Score measures the six dimensions that decide whether
          a site actually works, then turns the gaps into a prioritized plan. The grade is the diagnosis. The plan is
          the deliverable.
        </p>
      </section>

      <section className="space-y-8">
        <h2 className="text-fluid-h3 text-center">Six dimensions of a site that performs</h2>
        <div ref={pillarsRef} className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map(({ icon: Icon, title, line }) => (
            <motion.div
              key={title}
              className="pillar-card border-border/50 from-primary/5 hover:border-primary/30 rounded-2xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300 hover:shadow-lg"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="bg-primary/10 text-primary mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-bold">{title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{line}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="space-y-8">
        <div className="space-y-3 text-center">
          <h2 className="text-fluid-h3">What we measure that others miss</h2>
          <p className="text-muted-foreground mx-auto max-w-2xl">
            We run your current site and our build through a real browser, not just a snapshot of the code. That catches
            the things that quietly cost you reach and trust.
          </p>
        </div>
        <div ref={checksRef} className="grid gap-5 md:grid-cols-2">
          {checks.map(({ icon: Icon, title, line }) => (
            <motion.div
              key={title}
              className="check-card border-border/50 bg-card hover:border-primary/30 flex gap-4 rounded-2xl border p-6 transition-all duration-300"
              whileHover={{ y: -3 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="bg-primary/10 text-primary inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl">
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">{title}</h3>
                <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{line}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section
        ref={ctaRef}
        className="cta-content from-primary/10 via-primary/5 border-primary/20 space-y-6 rounded-2xl border bg-gradient-to-br to-transparent p-8 text-center md:p-12"
      >
        <h2 className="text-fluid-h3">See your site&rsquo;s Full Score</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          We&rsquo;ll run the same measurements on your current site and send you the report, free. You&rsquo;ll see
          exactly where it stands and what to fix first, whether or not we ever work together.
        </p>
        <motion.a
          href="https://calendly.com/garrett-garrettjohnlaw/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary inline-flex cursor-pointer items-center gap-2 font-medium hover:underline"
          whileHover={{ x: 4 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          Book a free Full Score review <ArrowRight className="h-4 w-4" />
        </motion.a>
      </section>
    </div>
  )
}
